import { supabase } from '../lib/supabase.js';

export class CouponManager {
  constructor() {
    this.coupons = [];
    this.filteredCoupons = [];
    this.isLoading = false;
    this.editingCoupon = null;
    this.searchTerm = '';
    this.filterStatus = 'all';
    this.filterType = 'all';
    this.filterExpiry = 'all';
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-xl font-bold text-gray-900">Coupon Management</h2>
            <p class="text-gray-600">Create and manage discount codes for customers</p>
          </div>
          <div class="flex space-x-3">
            <button 
              id="add-coupon-btn" 
              class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700"
            >
              Add Coupon
            </button>
            <button 
              id="refresh-coupons-btn" 
              class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
            >
              Refresh
            </button>
          </div>
        </div>

        <!-- Stats Overview -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-violet-50 p-4 rounded-lg border border-violet-200">
            <div class="flex items-center">
              <div class="p-2 bg-violet-100 rounded-lg">
                <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-violet-600">Total Coupons</p>
                <p class="text-2xl font-semibold text-violet-900" id="total-coupons-stat">-</p>
              </div>
            </div>
          </div>

          <div class="bg-green-50 p-4 rounded-lg border border-green-200">
            <div class="flex items-center">
              <div class="p-2 bg-green-100 rounded-lg">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-green-600">Active Coupons</p>
                <p class="text-2xl font-semibold text-green-900" id="active-coupons-stat">-</p>
              </div>
            </div>
          </div>

          <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div class="flex items-center">
              <div class="p-2 bg-blue-100 rounded-lg">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-blue-600">Total Uses</p>
                <p class="text-2xl font-semibold text-blue-900" id="total-uses-stat">-</p>
              </div>
            </div>
          </div>

          <div class="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div class="flex items-center">
              <div class="p-2 bg-orange-100 rounded-lg">
                <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-orange-600">Expiring Soon</p>
                <p class="text-2xl font-semibold text-orange-900" id="expiring-soon-stat">-</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Filters and Search -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input 
              type="text" 
              id="coupon-search" 
              placeholder="Search by code or description..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select 
              id="filter-status" 
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select 
              id="filter-type" 
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="all">All Types</option>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Expiry</label>
            <select 
              id="filter-expiry" 
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="all">All</option>
              <option value="active">Not Expired</option>
              <option value="expired">Expired</option>
              <option value="expiring-soon">Expiring Soon (7 days)</option>
              <option value="no-expiry">No Expiry</option>
            </select>
          </div>
        </div>

        <!-- Notification Area -->
        <div id="coupon-notification" class="hidden mb-6 p-4 rounded-md">
          <div id="coupon-notification-content" class="text-sm"></div>
        </div>

        <!-- Coupons Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody id="coupons-table-body" class="bg-white divide-y divide-gray-200">
              <tr>
                <td colspan="7" class="px-6 py-4 text-center text-gray-500">
                  Loading coupons...
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Add/Edit Coupon Modal -->
        <div id="coupon-modal" class="hidden fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div class="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div class="mt-3">
              <h3 class="text-lg font-medium text-gray-900 mb-4" id="modal-coupon-title">Add New Coupon</h3>
              
              <form id="coupon-form-element" class="space-y-4">
                <input type="hidden" id="coupon-id">
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Coupon Code *
                    </label>
                    <input 
                      type="text" 
                      id="coupon-code" 
                      required
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 uppercase"
                      placeholder="e.g., SAVE20"
                      pattern="[A-Z0-9]+"
                      title="Only uppercase letters and numbers allowed"
                    >
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Discount Type *
                    </label>
                    <select 
                      id="coupon-type" 
                      required
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="">Select type...</option>
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Discount Value *
                    </label>
                    <input 
                      type="number" 
                      id="coupon-value" 
                      required
                      step="0.01"
                      min="0.01"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="0.00"
                    >
                    <p class="text-xs text-gray-500 mt-1" id="value-hint">Enter the discount amount</p>
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Order Amount
                    </label>
                    <input 
                      type="number" 
                      id="coupon-min-order" 
                      step="0.01"
                      min="0"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="0.00"
                    >
                    <p class="text-xs text-gray-500 mt-1">Minimum order amount to use this coupon</p>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea 
                    id="coupon-description" 
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="Brief description of this coupon..."
                  ></textarea>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Usage Limit Per User
                    </label>
                    <input 
                      type="number" 
                      id="coupon-usage-limit-per-user" 
                      min="1"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="Leave empty for no per-user limit"
                    >
                    <p class="text-xs text-gray-500 mt-1">Maximum times each individual user can use this coupon</p>
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Usage Limit
                    </label>
                    <input 
                      type="number" 
                      id="coupon-usage-limit" 
                      min="1"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="Leave empty for unlimited"
                    >
                    <p class="text-xs text-gray-500 mt-1">Maximum number of times this coupon can be used</p>
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Expires At
                    </label>
                    <input 
                      type="datetime-local" 
                      id="coupon-expires-at" 
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                    <p class="text-xs text-gray-500 mt-1">Leave empty for no expiration</p>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <label class="flex items-center">
                      <input 
                        type="checkbox" 
                        id="coupon-is-active" 
                        checked
                        class="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                      >
                      <span class="ml-2 text-sm text-gray-700">Active (coupon can be used)</span>
                    </label>
                  </div>
                  
                  <!-- Message Display Area -->
                  <div id="coupon-form-message" class="hidden mb-4 p-3 rounded-md">
                    <!-- Success/error messages will be displayed here -->
                  </div>
                  
                  <div class="flex space-x-3">
                    <button 
                      type="submit" 
                      class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    >
                      Save Coupon
                    </button>
                    <button 
                      type="button" 
                      id="cancel-coupon-form-btn"
                      class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- User Usage Management Modal -->
        <div id="user-usage-modal" class="hidden fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div class="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div class="mt-3">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium text-gray-900" id="user-usage-modal-title">Manage Coupon Usage</h3>
                <button 
                  id="close-user-usage-modal-btn"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div id="user-usage-content" class="space-y-4">
                <div class="text-center py-8">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto mb-4"></div>
                  <div class="text-gray-500">Loading user usage data...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    await this.loadCoupons();

    // Add coupon button
    document.getElementById('add-coupon-btn').addEventListener('click', () => {
      this.showCouponForm();
    });

    // Refresh coupons button
    document.getElementById('refresh-coupons-btn').addEventListener('click', () => {
      this.loadCoupons();
    });

    // Search and filter events
    document.getElementById('coupon-search').addEventListener('input', (e) => {
      this.searchTerm = e.target.value.toLowerCase();
      this.filterCoupons();
    });

    document.getElementById('filter-status').addEventListener('change', (e) => {
      this.filterStatus = e.target.value;
      this.filterCoupons();
    });

    document.getElementById('filter-type').addEventListener('change', (e) => {
      this.filterType = e.target.value;
      this.filterCoupons();
    });

    document.getElementById('filter-expiry').addEventListener('change', (e) => {
      this.filterExpiry = e.target.value;
      this.filterCoupons();
    });

    // Modal events
    document.getElementById('cancel-coupon-form-btn').addEventListener('click', () => {
      this.hideCouponForm();
    });

    document.getElementById('coupon-form-element').addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.saveCoupon();
    });

    // Close modal when clicking outside
    document.getElementById('coupon-modal').addEventListener('click', (e) => {
      if (e.target.id === 'coupon-modal') {
        this.hideCouponForm();
      }
    });

    // User usage modal events
    document.getElementById('close-user-usage-modal-btn').addEventListener('click', () => {
      this.hideUserUsageModal();
    });

    document.getElementById('user-usage-modal').addEventListener('click', (e) => {
      if (e.target.id === 'user-usage-modal') {
        this.hideUserUsageModal();
      }
    });

    // Coupon type change event for value hint
    document.getElementById('coupon-type').addEventListener('change', (e) => {
      const hint = document.getElementById('value-hint');
      if (e.target.value === 'percentage') {
        hint.textContent = 'Enter percentage (e.g., 20 for 20% off)';
      } else if (e.target.value === 'fixed') {
        hint.textContent = 'Enter dollar amount (e.g., 5.00 for $5 off)';
      } else {
        hint.textContent = 'Enter the discount amount';
      }
    });

    // Auto-uppercase coupon code
    document.getElementById('coupon-code').addEventListener('input', (e) => {
      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    });
  }

  async loadCoupons() {
    this.isLoading = true;
    this.showLoadingState();

    try {
      const { data: coupons, error } = await supabase
        .from('coupons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      this.coupons = coupons || [];
      this.filteredCoupons = [...this.coupons];
      this.updateStats();
      this.renderCouponsTable();
    } catch (error) {
      this.showNotification('Error loading coupons: ' + error.message, 'error');
    } finally {
      this.isLoading = false;
    }
  }

  filterCoupons() {
    this.filteredCoupons = this.coupons.filter(coupon => {
      // Search filter
      if (this.searchTerm) {
        const searchableText = [
          coupon.code,
          coupon.description || ''
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(this.searchTerm)) {
          return false;
        }
      }

      // Status filter
      if (this.filterStatus !== 'all') {
        const isActive = coupon.is_active && (!coupon.expires_at || new Date(coupon.expires_at) > new Date());
        if (this.filterStatus === 'active' && !isActive) return false;
        if (this.filterStatus === 'inactive' && isActive) return false;
      }

      // Type filter
      if (this.filterType !== 'all' && coupon.type !== this.filterType) {
        return false;
      }

      // Expiry filter
      if (this.filterExpiry !== 'all') {
        const now = new Date();
        const expiryDate = coupon.expires_at ? new Date(coupon.expires_at) : null;
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        switch (this.filterExpiry) {
          case 'active':
            if (expiryDate && expiryDate <= now) return false;
            break;
          case 'expired':
            if (!expiryDate || expiryDate > now) return false;
            break;
          case 'expiring-soon':
            if (!expiryDate || expiryDate <= now || expiryDate > sevenDaysFromNow) return false;
            break;
          case 'no-expiry':
            if (expiryDate) return false;
            break;
        }
      }

      return true;
    });

    this.renderCouponsTable();
  }

  updateStats() {
    const total = this.coupons.length;
    const active = this.coupons.filter(c => c.is_active && (!c.expires_at || new Date(c.expires_at) > new Date())).length;
    const totalUses = this.coupons.reduce((sum, c) => sum + (c.used_count || 0), 0);
    
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const expiringSoon = this.coupons.filter(c => 
      c.is_active && 
      c.expires_at && 
      new Date(c.expires_at) > now && 
      new Date(c.expires_at) <= sevenDaysFromNow
    ).length;

    document.getElementById('total-coupons-stat').textContent = total;
    document.getElementById('active-coupons-stat').textContent = active;
    document.getElementById('total-uses-stat').textContent = totalUses;
    document.getElementById('expiring-soon-stat').textContent = expiringSoon;
  }

  showLoadingState() {
    const tbody = document.getElementById('coupons-table-body');
    tbody.innerHTML = `
      <tr>
        <td colspan="7" class="px-6 py-4 text-center text-gray-500">
          <div class="flex items-center justify-center">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600 mr-2"></div>
            Loading coupons...
          </div>
        </td>
      </tr>
    `;
  }

  renderCouponsTable() {
    const tbody = document.getElementById('coupons-table-body');
    
    if (this.filteredCoupons.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="7" class="px-6 py-4 text-center text-gray-500">
            ${this.searchTerm || this.filterStatus !== 'all' || this.filterType !== 'all' || this.filterExpiry !== 'all'
              ? 'No coupons match your search criteria'
              : 'No coupons found. Click "Add Coupon" to create your first coupon.'}
          </td>
        </tr>
      `;
      return;
    }

    tbody.innerHTML = this.filteredCoupons.map(coupon => this.renderCouponRow(coupon)).join('');
    this.bindCouponRowEvents();
  }

  renderCouponRow(coupon) {
    const isExpired = coupon.expires_at && new Date(coupon.expires_at) <= new Date();
    const isExpiringSoon = coupon.expires_at && !isExpired && new Date(coupon.expires_at) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    const isActive = coupon.is_active && !isExpired;
    
    const statusColor = isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    const statusText = isExpired ? 'Expired' : (isActive ? 'Active' : 'Inactive');
    
    const discountDisplay = coupon.type === 'percentage' 
      ? `${coupon.value}% off`
      : `$${parseFloat(coupon.value).toFixed(2)} off`;

    let usageDisplay = `${coupon.used_count || 0} uses`;
    if (coupon.usage_limit) {
      usageDisplay = `${coupon.used_count || 0} / ${coupon.usage_limit} total`;
    }
    if (coupon.usage_limit_per_user) {
      usageDisplay += ` (${coupon.usage_limit_per_user} per user)`;
    }

    const expiryDisplay = coupon.expires_at 
      ? new Date(coupon.expires_at).toLocaleDateString()
      : 'No expiry';

    const expiryClass = isExpired ? 'text-red-600' : (isExpiringSoon ? 'text-orange-600' : '');

    return `
      <tr class="hover:bg-gray-50">
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="font-mono font-bold text-violet-600">${coupon.code}</div>
          ${coupon.min_order_amount > 0 ? `<div class="text-xs text-gray-500">Min: $${parseFloat(coupon.min_order_amount).toFixed(2)}</div>` : ''}
        </td>
        <td class="px-6 py-4">
          <div class="text-sm text-gray-900">${coupon.description || '-'}</div>
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="text-sm font-medium text-gray-900">${discountDisplay}</div>
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="text-sm text-gray-900">${usageDisplay}</div>
          ${coupon.usage_limit && coupon.used_count >= coupon.usage_limit ? 
            '<div class="text-xs text-red-500">Limit reached</div>' : ''}
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="text-sm ${expiryClass}">${expiryDisplay}</div>
          ${isExpiringSoon ? '<div class="text-xs text-orange-500">Expiring soon</div>' : ''}
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor}">
            ${statusText}
          </span>
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
          <button 
            class="edit-coupon-btn text-violet-600 hover:text-violet-800"
            data-coupon-id="${coupon.id}"
          >
            Edit
          </button>
          <button 
            class="toggle-coupon-btn text-${isActive ? 'yellow' : 'green'}-600 hover:text-${isActive ? 'yellow' : 'green'}-800"
            data-coupon-id="${coupon.id}"
            data-current-status="${coupon.is_active}"
          >
            ${isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button 
            class="duplicate-coupon-btn text-blue-600 hover:text-blue-800"
            data-coupon-id="${coupon.id}"
          >
            Duplicate
          </button>
          <button 
            class="manage-users-btn text-purple-600 hover:text-purple-800"
            data-coupon-id="${coupon.id}"
            onclick="console.log('Direct onclick fired!'); window.dashboardManager.couponManager.showUserUsageModal('${coupon.id}');"
          >
            Manage Users
          </button>
          <button 
            class="delete-coupon-btn text-red-600 hover:text-red-800"
            data-coupon-id="${coupon.id}"
          >
            Delete
          </button>
        </td>
      </tr>
    `;
  }

  bindCouponRowEvents() {
    console.log('🔧 Binding coupon row events...');
    const manageUsersBtns = document.querySelectorAll('.manage-users-btn');
    console.log('📊 Found manage users buttons:', manageUsersBtns.length);
    
    // Edit buttons
    document.querySelectorAll('.edit-coupon-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const couponId = e.target.dataset.couponId;
        this.editCoupon(couponId);
      });
    });

    // Toggle status buttons
    document.querySelectorAll('.toggle-coupon-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const couponId = e.target.dataset.couponId;
        const currentStatus = e.target.dataset.currentStatus === 'true';
        this.toggleCouponStatus(couponId, !currentStatus);
      });
    });

    // Duplicate buttons
    document.querySelectorAll('.duplicate-coupon-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const couponId = e.target.dataset.couponId;
        this.duplicateCoupon(couponId);
      });
    });

    // Manage users buttons
    document.querySelectorAll('.manage-users-btn').forEach((btn, index) => {
      console.log(`🎯 Binding event to manage users button ${index + 1}`);
      btn.addEventListener('click', (e) => {
        const couponId = e.target.dataset.couponId;
        console.log('Manage Users button clicked for coupon ID:', couponId);
        console.log('📝 Button element:', e.target);
        console.log('🎫 All dataset:', e.target.dataset);
        this.showUserUsageModal(couponId);
      });
    });

    // Delete buttons
    document.querySelectorAll('.delete-coupon-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const couponId = e.target.dataset.couponId;
        this.deleteCoupon(couponId);
      });
    });
  }

  showCouponForm(coupon = null) {
    const modal = document.getElementById('coupon-modal');
    const title = document.getElementById('modal-coupon-title');
    
    this.hideCouponFormMessages();
    
    modal.classList.remove('hidden');
    
    if (coupon) {
      title.textContent = 'Edit Coupon';
      this.populateCouponForm(coupon);
    } else {
      title.textContent = 'Add New Coupon';
      this.clearCouponForm();
    }
  }

  hideCouponForm() {
    document.getElementById('coupon-modal').classList.add('hidden');
    this.clearCouponForm();
    this.hideCouponFormMessages();
    this.editingCoupon = null;
  }

  populateCouponForm(coupon) {
    document.getElementById('coupon-id').value = coupon.id;
    document.getElementById('coupon-code').value = coupon.code;
    document.getElementById('coupon-type').value = coupon.type;
    document.getElementById('coupon-value').value = coupon.value;
    document.getElementById('coupon-min-order').value = coupon.min_order_amount || '';
    document.getElementById('coupon-description').value = coupon.description || '';
    document.getElementById('coupon-usage-limit').value = coupon.usage_limit || '';
    document.getElementById('coupon-usage-limit-per-user').value = coupon.usage_limit_per_user || '';
    document.getElementById('coupon-is-active').checked = coupon.is_active;
    
    if (coupon.expires_at) {
      const date = new Date(coupon.expires_at);
      const formattedDate = date.toISOString().slice(0, 16); // Format for datetime-local
      document.getElementById('coupon-expires-at').value = formattedDate;
    }
    
    this.editingCoupon = coupon;
  }

  clearCouponForm() {
    document.getElementById('coupon-form-element').reset();
    document.getElementById('coupon-id').value = '';
    document.getElementById('coupon-is-active').checked = true;
    this.hideCouponFormMessages();
  }

  async saveCoupon() {
    this.hideCouponFormMessages();
    
    const couponData = {
      code: document.getElementById('coupon-code').value.toUpperCase(),
      type: document.getElementById('coupon-type').value,
      value: parseFloat(document.getElementById('coupon-value').value),
      min_order_amount: parseFloat(document.getElementById('coupon-min-order').value) || 0,
      description: document.getElementById('coupon-description').value || null,
      usage_limit: parseInt(document.getElementById('coupon-usage-limit').value) || null,
      usage_limit_per_user: parseInt(document.getElementById('coupon-usage-limit-per-user').value) || null,
      expires_at: document.getElementById('coupon-expires-at').value || null,
      is_active: document.getElementById('coupon-is-active').checked
    };

    // Validation
    if (couponData.type === 'percentage' && couponData.value > 100) {
      this.showCouponFormMessage('Percentage discount cannot exceed 100%', 'error');
      return;
    }

    try {
      const couponId = document.getElementById('coupon-id').value;
      
      if (couponId) {
        // Update existing coupon
        const { error } = await supabase
          .from('coupons')
          .update(couponData)
          .eq('id', couponId);

        if (error) throw error;
        this.showNotification('Coupon updated successfully!', 'success');
      } else {
        // Create new coupon
        const { error } = await supabase
          .from('coupons')
          .insert([couponData]);

        if (error) throw error;
        this.showNotification('Coupon created successfully!', 'success');
      }

      await this.loadCoupons();
      this.hideCouponForm();
    } catch (error) {
      if (error.code === '23505') {
        this.showCouponFormMessage('A coupon with this code already exists. Please choose a different code.', 'error');
      } else {
        this.showCouponFormMessage('Error saving coupon: ' + error.message, 'error');
      }
    }
  }

  editCoupon(couponId) {
    const coupon = this.coupons.find(c => c.id === couponId);
    if (coupon) {
      this.showCouponForm(coupon);
    }
  }

  async toggleCouponStatus(couponId, newStatus) {
    try {
      const { error } = await supabase
        .from('coupons')
        .update({ is_active: newStatus })
        .eq('id', couponId);

      if (error) throw error;

      await this.loadCoupons();
      this.showNotification(`Coupon ${newStatus ? 'activated' : 'deactivated'} successfully!`, 'success');
    } catch (error) {
      this.showNotification('Error updating coupon status: ' + error.message, 'error');
    }
  }

  async duplicateCoupon(couponId) {
    const coupon = this.coupons.find(c => c.id === couponId);
    if (!coupon) return;

    const newCode = prompt('Enter new coupon code:', coupon.code + '_COPY');
    if (!newCode) return;

    try {
      const duplicateData = {
        code: newCode.toUpperCase(),
        type: coupon.type,
        value: coupon.value,
        min_order_amount: coupon.min_order_amount,
        description: `Copy of: ${coupon.description || coupon.code}`,
        usage_limit: coupon.usage_limit,
        expires_at: coupon.expires_at,
        is_active: false // Start inactive for safety
      };

      const { error } = await supabase
        .from('coupons')
        .insert([duplicateData]);

      if (error) throw error;

      await this.loadCoupons();
      this.showNotification('Coupon duplicated successfully!', 'success');
    } catch (error) {
      if (error.code === '23505') {
        this.showNotification('A coupon with this code already exists.', 'error');
      } else {
        this.showNotification('Error duplicating coupon: ' + error.message, 'error');
      }
    }
  }

  async deleteCoupon(couponId) {
    const coupon = this.coupons.find(c => c.id === couponId);
    if (!coupon) return;

    if (!confirm(`Are you sure you want to delete coupon "${coupon.code}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('coupons')
        .delete()
        .eq('id', couponId);

      if (error) throw error;

      await this.loadCoupons();
      this.showNotification('Coupon deleted successfully!', 'success');
    } catch (error) {
      this.showNotification('Error deleting coupon: ' + error.message, 'error');
    }
  }

  hideCouponFormMessages() {
    const messageDiv = document.getElementById('coupon-form-message');
    if (messageDiv) {
      messageDiv.classList.add('hidden');
      messageDiv.textContent = '';
      messageDiv.className = 'hidden mb-4 p-3 rounded-md';
    }
  }

  showCouponFormMessage(message, type = 'info') {
    const messageDiv = document.getElementById('coupon-form-message');
    if (messageDiv) {
      const colorClasses = {
        'info': 'bg-violet-50 text-violet-800 border border-violet-200',
        'success': 'bg-green-50 text-green-800 border border-green-200',
        'warning': 'bg-yellow-50 text-yellow-800 border border-yellow-200',
        'error': 'bg-red-50 text-red-800 border border-red-200'
      };

      messageDiv.textContent = message;
      messageDiv.className = `mb-4 p-3 rounded-md ${colorClasses[type] || colorClasses.info}`;
      messageDiv.classList.remove('hidden');
    }
  }

  showNotification(message, type = 'info') {
    const notificationDiv = document.getElementById('coupon-notification');
    const contentDiv = document.getElementById('coupon-notification-content');
    
    if (!notificationDiv || !contentDiv) return;

    const colorClasses = {
      'info': 'bg-violet-50 text-violet-800 border border-violet-200',
      'success': 'bg-green-50 text-green-800 border border-green-200',
      'warning': 'bg-yellow-50 text-yellow-800 border border-yellow-200',
      'error': 'bg-red-50 text-red-800 border border-red-200'
    };

    notificationDiv.className = `mb-6 p-4 rounded-md ${colorClasses[type] || colorClasses.info}`;
    contentDiv.innerHTML = `
      <div class="flex items-center">
        <div class="flex-1">${message}</div>
        <button 
          onclick="document.getElementById('coupon-notification').classList.add('hidden')" 
          class="ml-4 text-current hover:opacity-75"
        >
          ✕
        </button>
      </div>
    `;
    
    notificationDiv.classList.remove('hidden');

    // Auto-hide success and info messages after 5 seconds
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        notificationDiv.classList.add('hidden');
      }, 5000);
    }
  }

  async showUserUsageModal(couponId) {
    console.log('🚀 showUserUsageModal called with couponId:', couponId);
    const coupon = this.coupons.find(c => c.id === couponId);
    console.log('🎫 Found coupon:', coupon);
    if (!coupon) {
      console.error('❌ Coupon not found for ID:', couponId);
      this.showNotification('Coupon not found', 'error');
      return;
    }

    // Remove any existing custom modal
    const existingCustomModal = document.getElementById('custom-user-usage-modal');
    if (existingCustomModal) {
      existingCustomModal.remove();
    }
    
    // Create a completely new modal element with inline styles
    const modalHtml = `
      <div id="custom-user-usage-modal" style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9999;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: 3rem;
        overflow-y: auto;
      ">
        <div style="
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          max-width: 80rem;
          width: 95%;
          margin: 0 auto;
          position: relative;
          padding: 1.5rem;
          max-height: 90vh;
          overflow-y: auto;
        ">
          <!-- Modal Header -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
            <h3 style="font-size: 1.25rem; font-weight: 600; color: #111827;">
              Manage Users for Coupon: ${coupon.code}
            </h3>
            <button 
              onclick="dashboardManager.couponManager.hideCustomUserUsageModal()" 
              style="color: #9ca3af; font-size: 1.5rem; background: none; border: none; cursor: pointer;"
            >
              ✕
            </button>
          </div>
          
          <!-- Search Section -->
          <div style="background-color: #dbeafe; padding: 1rem; border-radius: 0.5rem; margin-bottom: 1.5rem; border: 1px solid #3b82f6;">
            <h4 style="font-weight: 600; color: #1e40af; margin-bottom: 1rem; font-size: 1rem;">🔍 Search & Block Customers</h4>
            <p style="font-size: 0.875rem; color: #1e3a8a; margin-bottom: 1rem;">Search for any customer to block them from using this coupon, even if they haven't used it yet.</p>
            <div style="display: flex; gap: 0.5rem; margin-bottom: 0.75rem;">
              <input type="text" id="customer-search-input-${couponId}" placeholder="Search by email or phone number..." style="flex: 1; padding: 0.75rem; border: 1px solid #3b82f6; border-radius: 0.375rem; font-size: 0.875rem;" onkeypress="if(event.key==='Enter') dashboardManager.couponManager.searchCustomersForCoupon('${couponId}')">
              <button onclick="dashboardManager.couponManager.searchCustomersForCoupon('${couponId}')" style="background-color: #3b82f6; color: white; padding: 0.75rem 1rem; border-radius: 0.375rem; border: none; cursor: pointer; font-size: 0.875rem; font-weight: 500;">🔍 Search</button>
              <button onclick="document.getElementById('customer-search-input-${couponId}').value=''; document.getElementById('customer-search-results-${couponId}').style.display='none';" style="background-color: #6b7280; color: white; padding: 0.75rem 1rem; border-radius: 0.375rem; border: none; cursor: pointer; font-size: 0.875rem; font-weight: 500;">Clear</button>
            </div>
          </div>
          
          <!-- Search Results Section -->
          <div id="customer-search-results-${couponId}" style="margin-bottom: 1.5rem; display: none;">
            <h4 style="font-weight: 600; color: #374151; margin-bottom: 1rem; font-size: 1rem;">🔍 Search Results</h4>
            <div id="customer-search-results-content-${couponId}">
              <!-- Search results will be populated here -->
            </div>
          </div>
          
          <!-- Existing Users Section -->
          <div>
            <h4 style="font-weight: 600; color: #374151; margin-bottom: 1rem; font-size: 1rem;">👥 Users Who Have Used This Coupon</h4>
            <div id="existing-users-section-content">
              <div style="text-align: center; padding: 2rem 0;">
                <div style="width: 32px; height: 32px; border: 3px solid #8b5cf6; border-top: 3px solid transparent; border-radius: 50%; margin: 0 auto 1rem auto; animation: spin 1s linear infinite;"></div>
                <div style="color: #6b7280;">Loading user usage data...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>`;

    // DEBUG: Log the modal HTML to console
    console.log('🐛 Generated modal HTML:', modalHtml);
    
    // Insert the modal into the body
    document.body.insertAdjacentHTML('beforeend', modalHtml);
    
    console.log('✅ Custom modal created and should be visible');
    console.log('✅ Modal added to DOM. Search section should be visible with blue background.');

    
    await this.loadCouponUserUsage(couponId);
  }

  hideUserUsageModal() {
    this.hideCustomUserUsageModal();
  }
  
  hideCustomUserUsageModal() {
    const customModal = document.getElementById('custom-user-usage-modal');
    if (customModal) {
      customModal.remove();
      console.log('✅ Custom modal removed');
    }
  }

  async searchCustomersForCoupon(couponId) {
    const searchTerm = document.getElementById(`customer-search-input-${couponId}`).value.trim();
    if (!searchTerm) {
      this.showError('Please enter a search term');
      return;
    }

    const searchBtn = document.querySelector(`button[onclick*="searchCustomersForCoupon('${couponId}')"]`);
    const resultsDiv = document.getElementById(`customer-search-results-${couponId}`);
    
    if (searchBtn) {
      searchBtn.disabled = true;
      searchBtn.textContent = 'Searching...';
    }

    try {
      const { data: customers, error } = await supabase
        .from('customers')
        .select('*')
        .or(`email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      const resultsContentDiv = document.getElementById(`customer-search-results-content-${couponId}`);
      
      if (customers.length === 0) {
        resultsContentDiv.innerHTML = `
          <div style="padding: 1rem; text-align: center; color: #6b7280;">
            No customers found matching "${searchTerm}"
          </div>
        `;
      } else {
        resultsContentDiv.innerHTML = `
          <div style="display: flex; flex-direction: column; gap: 0.5rem;">
            ${customers.map(customer => `
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; background-color: #f9fafb; border-radius: 0.375rem; border: 1px solid #e5e7eb;">
                <div style="flex: 1;">
                  <div style="font-weight: 500; color: #111827;">${customer.full_name}</div>
                  <div style="font-size: 0.875rem; color: #4b5563;">${customer.email}</div>
                  <div style="font-size: 0.75rem; color: #6b7280;">${customer.phone}</div>
                </div>
                <button 
                  onclick="dashboardManager.couponManager.blockCustomerFromCoupon('${customer.id}', '${couponId}', true)"
                  style="background-color: #dc2626; color: white; padding: 0.25rem 0.75rem; border-radius: 0.25rem; font-size: 0.875rem; border: none; cursor: pointer;"
                  onmouseover="this.style.backgroundColor='#b91c1c'"
                  onmouseout="this.style.backgroundColor='#dc2626'"
                >
                  Block
                </button>
              </div>
            `).join('')}
          </div>
        `;
      }

      resultsDiv.style.display = 'block';
    } catch (error) {
      this.showError('Error searching customers: ' + error.message);
    } finally {
      if (searchBtn) {
        searchBtn.disabled = false;
        searchBtn.textContent = '🔍 Search';
      }
    }
  }

  async blockCustomerFromCoupon(customerId, couponId) {
    try {
      // Check if usage record already exists
      const { data: existingUsage, error: checkError } = await supabase
        .from('coupon_user_usage')
        .select('id, is_blocked')
        .eq('coupon_id', couponId)
        .eq('customer_id', customerId)
        .single();

      if (checkError && checkError.code === 'PGRST116') {
        // No existing record, create new blocked record
        const { error: insertError } = await supabase
          .from('coupon_user_usage')
          .insert({
            coupon_id: couponId,
            customer_id: customerId,
            usage_count: 0,
            is_blocked: true
          });

        if (insertError) throw insertError;
        this.showNotification('Customer blocked from using this coupon', 'success');
      } else if (!checkError && existingUsage) {
        // Update existing record to blocked
        const { error: updateError } = await supabase
          .from('coupon_user_usage')
          .update({ is_blocked: true })
          .eq('id', existingUsage.id);

        if (updateError) throw updateError;
        this.showNotification('Customer blocked from using this coupon', 'success');
      } else {
        throw new Error('Failed to check existing usage record');
      }

      // Refresh the search results
      this.searchCustomersForCoupon();
      
    } catch (error) {
      console.error('Error blocking customer:', error);
      this.showNotification('Error blocking customer: ' + error.message, 'error');
    }
  }

  clearCustomerSearch() {
    document.getElementById('user-search-input').value = '';
    document.getElementById('customer-search-results').classList.add('hidden');
  }

  showError(message) {
    console.error('CouponManager Error:', message);
    alert('Error: ' + message);
  }

  async loadCouponUserUsage(couponId) {
    try {
      const { data: userUsages, error } = await supabase
        .from('coupon_user_usage')
        .select(`
          *,
          customers (
            full_name,
            email,
            phone
          ),
          coupons (
            code,
            description
          )
        `)
        .eq('coupon_id', couponId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      this.renderUserUsageTable(userUsages || []);
    } catch (error) {
      // Update only the existing users section, not the whole modal
      const content = document.getElementById('existing-users-section-content');
      if (content) {
        content.innerHTML = `
          <div style="text-align: center; padding: 2rem 0; color: #ef4444;">
            Error loading user usage data: ${error.message}
          </div>
        `;
      }
    }
  }

  clearCustomerSearch(couponId) {
    const searchInput = document.getElementById(`customer-search-input-${couponId}`);
    const resultsDiv = document.getElementById(`customer-search-results-${couponId}`);
    
    if (searchInput) searchInput.value = '';
    if (resultsDiv) resultsDiv.style.display = 'none';
    
    console.log('🧹 Cleared customer search for coupon:', couponId);
  }

  renderUserUsageTable(userUsages) {
    // Update only the existing users section content, not the whole modal
    const content = document.getElementById('existing-users-section-content');
    if (!content) return;
    
    if (userUsages.length === 0) {
      content.innerHTML = `
        <div style="text-align: center; padding: 2rem 0; color: #6b7280;">
          <div style="font-size: 1.125rem; margin-bottom: 0.5rem;">📊</div>
          <div>No users have used this coupon yet.</div>
        </div>
      `;
      return;
    }

    content.innerHTML = `
      <div style="margin-bottom: 1rem;">
        <p style="font-size: 0.875rem; color: #4b5563;">
          Block users from using this coupon. Blocked users will not be able to apply this coupon code in future orders.
        </p>
      </div>
      
      <div style="overflow-x: auto;">
        <table style="min-width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb;">
          <thead style="background-color: #f9fafb;">
            <tr>
              <th style="padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb;">
                Customer
              </th>
              <th style="padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb;">
                Contact
              </th>
              <th style="padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb;">
                Usage Count
              </th>
              <th style="padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb;">
                First Used
              </th>
              <th style="padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb;">
                Status
              </th>
              <th style="padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #e5e7eb;">
                Actions
              </th>
            </tr>
          </thead>
          <tbody style="background-color: white;">
            ${userUsages.map(usage => this.renderUserUsageRow(usage)).join('')}
          </tbody>
        </table>
      </div>
    `;

    // Bind toggle events for the new rows
    this.bindUserUsageEvents();
  }

  renderUserUsageRow(usage) {
    const isBlocked = usage.is_blocked;
    const statusColor = isBlocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800';
    const statusText = isBlocked ? 'Blocked' : 'Allowed';
    
    return `
      <tr style="border-bottom: 1px solid #e5e7eb; ${isBlocked ? 'opacity: 0.6;' : ''}" onmouseover="this.style.backgroundColor='#f9fafb'" onmouseout="this.style.backgroundColor='white'">
        <td style="padding: 1rem 1.5rem; white-space: nowrap;">
          <div style="font-weight: 500; color: #111827;">${usage.customers?.full_name || 'Unknown'}</div>
        </td>
        <td style="padding: 1rem 1.5rem; white-space: nowrap;">
          <div style="font-size: 0.875rem; color: #4b5563;">${usage.customers?.email || 'No email'}</div>
          <div style="font-size: 0.875rem; color: #6b7280;">${usage.customers?.phone || 'No phone'}</div>
        </td>
        <td style="padding: 1rem 1.5rem; white-space: nowrap;">
          <div style="font-size: 0.875rem; font-weight: 500; color: #111827;">${usage.usage_count}</div>
        </td>
        <td style="padding: 1rem 1.5rem; white-space: nowrap;">
          <div style="font-size: 0.875rem; color: #6b7280;">${new Date(usage.created_at).toLocaleDateString()}</div>
        </td>
        <td style="padding: 1rem 1.5rem; white-space: nowrap;">
          <span style="display: inline-flex; padding: 0.25rem 0.5rem; font-size: 0.75rem; font-weight: 600; border-radius: 9999px; background-color: ${statusColor}; color: ${statusTextColor};">
            ${statusText}
          </span>
        </td>
        <td style="padding: 1rem 1.5rem; white-space: nowrap; font-size: 0.875rem; font-weight: 500;">
          <button 
            onclick="dashboardManager.couponManager.toggleUserCouponBlock('${usage.id}', ${!isBlocked}, '${usage.customers?.full_name || 'Unknown'}', '${usage.coupons?.code || 'Unknown'}')"
            style="color: ${isBlocked ? '#059669' : '#dc2626'}; background: none; border: none; cursor: pointer; font-weight: 500;"
            onmouseover="this.style.opacity='0.8'"
            onmouseout="this.style.opacity='1'"
            data-usage-id="${usage.id}"
            data-customer-name="${usage.customers?.full_name || 'Unknown'}"
            data-coupon-code="${usage.coupons?.code || 'Unknown'}"
            data-current-blocked="${isBlocked}"
          >
            ${isBlocked ? 'Unblock' : 'Block'} User
          </button>
          ${!usage.usageRecord ? `
            <div style="font-size: 0.75rem; color: #9ca3af; margin-top: 0.25rem;">
              Proactive block
            </div>
          ` : ''}
        </td>
      </tr>
    `;
  }

  bindUserUsageEvents() {
    document.querySelectorAll('.toggle-user-block-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const usageId = e.target.dataset.usageId;
        const customerName = e.target.dataset.customerName;
        const couponCode = e.target.dataset.couponCode;
        const currentBlocked = e.target.dataset.currentBlocked === 'true';
        
        this.toggleUserCouponBlock(usageId, !currentBlocked, customerName, couponCode);
      });
    });
  }

  async toggleUserCouponBlock(usageId, newBlockedStatus, customerName, couponCode) {
    const action = newBlockedStatus ? 'block' : 'unblock';
    const message = `Are you sure you want to ${action} ${customerName} from using coupon ${couponCode}?`;
    
    if (!confirm(message)) return;

    try {
      const { error } = await supabase
        .from('coupon_user_usage')
        .update({ is_blocked: newBlockedStatus })
        .eq('id', usageId);

      if (error) throw error;

      this.showNotification(
        `Successfully ${newBlockedStatus ? 'blocked' : 'unblocked'} ${customerName} from using coupon ${couponCode}`, 
        'success'
      );

      // Reload the user usage table to reflect changes
      const modal = document.getElementById('user-usage-modal');
      if (modal && !modal.classList.contains('hidden')) {
        const couponId = this.getSelectedCouponIdFromModal();
        if (couponId) {
          await this.loadCouponUserUsage(couponId);
        }
      }
    } catch (error) {
      this.showNotification(`Error updating block status: ${error.message}`, 'error');
    }
  }

  getSelectedCouponIdFromModal() {
    // Extract coupon ID from the currently displayed modal
    const title = document.getElementById('user-usage-modal-title');
    if (!title) return null;
    
    const titleText = title.textContent;
    const couponCode = titleText.split(': ')[1];
    const coupon = this.coupons.find(c => c.code === couponCode);
    return coupon?.id || null;
  }
}